import React from "react";
import { useContext, useEffect, useState } from "react";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import OrderDiscountsList from "./OrderDiscountsList";
import Pagination from "../Pagination";
import { useTranslation } from "react-i18next";
import AuthContext from "context/AuthContext";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";
import dayjs from "dayjs";

const OrderDiscounts = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const manager =
    user?.permission?.find((e) => e.manager === "order_manager") ?? {};

  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [filterData, setFilterData] = useState({
    startDate: "",
    endDate: "",
    isReset: false,
    status: "",
    discountType: "",
    requestFor: "",
    isFilter: false,
    searchKey: "",
  });
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });
  const [sort, setSort] = useState({
    sortKey: "createdAt",
    sortType: "desc",
  });

  const { startDate, endDate, status, discountType, requestFor, searchKey } =
    filterData;

  const fetchOrderDiscounts = async (data) => {
    try {
      const payload = {
        page,
        pageSize,
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
        keyword: searchKey ? searchKey.trim() : null,
        status: status || null,
        discountType: discountType || null,
        requestFor: requestFor || null,
        sortKey: sort.sortKey,
        sortType: sort.sortType,
      };
      const path = apiPath.orderDiscounts;
      const result = await apiGet(path, payload);
      const response = result?.data?.results;
      const resultStatus = result?.data?.success;
      setList(response?.docs);
      setPaginationObj({
        ...paginationObj,
        page: resultStatus ? response.page : null,
        pageCount: resultStatus ? response.totalPages : null,
        perPageItem: resultStatus ? response?.docs.length : null,
        totalItems: resultStatus ? response.totalDocs : null,
      });
    } catch (error) {
      console.error("error in get order discounts list==>>>>", error.message);
    }
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPage(newPage);
  };

  const handleDateChange = (start, end) => {
    setPage(1);
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true,
    });
  };

  const handleChange = (event) => {
    setFilterData({
      ...filterData,
      isFilter: true,
      [event.target.name]: event.target.value,
    });
    setPage(1);
  };

  const handleReset = () => {
    setFilterData({
      startDate: "",
      endDate: "",
      isReset: true,
      isFilter: false,
      status: "",
      discountType: "",
      requestFor: "",
      searchKey: "",
    });
    setPage(1);
  };

  useEffect(() => {
    fetchOrderDiscounts();
    // eslint-disable-next-line
  }, [page, filterData, sort]);

  return (
    <div>
      <div className="bg-white">
        <div className="px-3 py-4">
          <div className="bg-white border border-[#E9EDF9] ">
            <form className="border-b border-b-[#E3E3E3] px-4 py-3">
              {/* Top Row: Date Range and Search */}
              <div className="flex flex-wrap items-center justify-between mb-3">
                <div className="flex items-center">
                  <ODateRangePicker
                    handleDateChange={handleDateChange}
                    isReset={filterData?.isReset}
                    setIsReset={setFilterData}
                  />
                </div>
                <div className="flex items-center">
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 flex items-center pl-3 mr-3 pointer-events-none">
                      <svg
                        aria-hidden="true"
                        className="w-4 h-4 text-[#A5A5A5] dark:text-gray-40"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {!filterData?.searchKey && (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        )}
                      </svg>
                    </div>
                    <input
                      type="search"
                      id="default-search"
                      name="searchKey"
                      className="block w-full p-2 outline-none text-sm text-gray-900 min-w-[300px] rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder="Search order discounts..."
                      value={searchKey}
                      required
                      onChange={(e) => handleChange(e)}
                    />
                  </div>
                </div>
              </div>

              {/* Bottom Row: Other Filters and Reset Button */}
              <div className="flex flex-wrap items-center justify-between">
                <div className="flex items-center flex-wrap">
                  <div className="flex items-center ml-0 mr-3 mb-2">
                    <select
                      id="status"
                      name="status"
                      className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      value={status}
                      onChange={handleChange}
                    >
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="accepted">Accepted</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div className="flex items-center mr-3 mb-2">
                    <select
                      id="discountType"
                      name="discountType"
                      className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      value={discountType}
                      onChange={handleChange}
                    >
                      <option value="">All Types</option>
                      <option value="percentage">Percentage</option>
                      <option value="amount">Fixed Amount</option>
                    </select>
                  </div>

                  <div className="flex items-center mr-3 mb-2">
                    <select
                      id="requestFor"
                      name="requestFor"
                      className="block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      value={requestFor}
                      onChange={handleChange}
                    >
                      <option value="">All Request Types</option>
                      <option value="price">Price</option>
                      <option value="commission">Commission</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="bg-[#8FC150] text-sm px-8 py-2 rounded-lg items-center border border-transparent text-white hover:bg-[#8FC150]"
                  >
                    {t("O_RESET")}
                  </button>
                </div>
              </div>
            </form>
            <OrderDiscountsList
              page={page}
              paginationObj={paginationObj}
              sort={sort}
              setSort={setSort}
              list={list}
              fetchOrderDiscounts={fetchOrderDiscounts}
              manager={manager}
            />
            {paginationObj?.totalItems?.length !== 0 && (
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                page={page}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDiscounts;
